import {
  selectHasShownIntroPopups,
  selectPopups,
  selectConfDialog,
  selectCuttableMountingRailHintCheckPending,
} from './popupsSelectors';
import { selectTac } from '../tac/tacSelectors';
import {
  actionConfClosed,
  actionHandlePopupOnAddItem,
  actionHandlePopupOnUpdateItem,
  actionSetHasShownIntroPopups,
  actionSetIntroPopupsVisible,
  actionSetCuttableMountingRailHintCheckPending,
} from './popupsActions';
import { Thunk } from '../../generalTypes';
import { MountingRailThunkParams } from './popupsTypes';
import productsServiceCommon, {
  isPegboard,
  shouldShowDoorHint,
} from '../../services/products';
import { extendableConfItem } from './extendableConfItem';
import { range } from '../tac/range';
import { ITEMS } from '../../constants';
import { LEFT, RIGHT } from '../../components/Popup/alignments';
import constants from '../../settings/constants';

export const thunkConditionalDisplayIntroPopups: Thunk =
  () => (dispatch, getState) => {
    const hasShownIntroPopups = selectHasShownIntroPopups(getState());
    if (!hasShownIntroPopups) {
      dispatch(actionSetIntroPopupsVisible(true));
      dispatch(actionSetHasShownIntroPopups());
    }
  };

export const thunkConfClosed: Thunk<any> =
  (closedId: number | undefined) => (dispatch, getState) => {
    dispatch(actionConfClosed(closedId));
    closedId && dispatch(actionSetCuttableMountingRailHintCheckPending(true));
  };

export const thunkHandlePopupOnAddItem: Thunk<any> =
  (item, meta) => (dispatch, getState) => {
    const popUpsState = selectPopups(getState());

    if (!popUpsState.hasShownPegboardHint && isPegboard(item.id)) {
      return dispatch(
        actionHandlePopupOnAddItem({
          showPegboardHint: item,
          hasShownPegboardHint: true,
          hasHadUserInteraction: true,
        })
      );
    }

    if (shouldShowDoorHint(item.id)) {
      return dispatch(
        actionHandlePopupOnAddItem({
          showDoorsHint: item,
          hasHadUserInteraction: true,
        })
      );
    }

    if (!popUpsState.hasShownExtendableConf) {
      const confItem = extendableConfItem(item, meta);
      if (confItem) {
        return dispatch(
          actionHandlePopupOnAddItem({
            showExtendableConf: confItem,
            hasShownExtendableConf: true,
            hasHadUserInteraction: true,
          })
        );
      }
    }

    dispatch(actionHandlePopupOnAddItem({ hasHadUserInteraction: true }));
  };

export const thunkHandlePopupOnUpdateItem: Thunk<any> =
  (item, meta) => (dispatch, getState) => {
    const popUpsState = selectPopups(getState());

    if (!popUpsState.hasShownPegboardHint && isPegboard(item.id)) {
      return dispatch(
        actionHandlePopupOnUpdateItem({
          showPegboardHint: item,
          hasShownPegboardHint: true,
          hasHadUserInteraction:
            popUpsState.hasHadUserInteraction || !meta?.automatedUpdate,
        })
      );
    }

    if (!popUpsState.hasShownExtendableConf) {
      const confItem = extendableConfItem(item, meta);
      if (confItem) {
        return dispatch(
          actionHandlePopupOnUpdateItem({
            showExtendableConf: confItem,
            hasShownExtendableConf: true,
            hasHadUserInteraction:
              popUpsState.hasHadUserInteraction || !meta?.automatedUpdate,
          })
        );
      }
    }

    if (popUpsState.hasShownExtendableConf) {
      return dispatch(
        actionHandlePopupOnUpdateItem({
          showExtendableConf: null,
          hasShownExtendableConf: true,
          hasHadUserInteraction:
            popUpsState.hasHadUserInteraction || !meta?.automatedUpdate,
        })
      );
    }

    dispatch(
      actionHandlePopupOnUpdateItem({
        hasHadUserInteraction:
          popUpsState.hasHadUserInteraction || !meta?.automatedUpdate,
      })
    );
  };

export const thunkHandlePopupOnRemoveItem: Thunk<any> =
  () => (dispatch, getState) => {
    const popUpsState = selectPopups(getState());

    if (popUpsState.hasShownExtendableConf) {
      dispatch(
        actionHandlePopupOnUpdateItem({
          showExtendableConf: null,
          hasShownExtendableConf: true,
        })
      );
    }
  };

export const thunkHandlePopupOnTacLoad: Thunk<any> = () => dispatch => {
  dispatch(
    actionHandlePopupOnUpdateItem({
      showExtendableConf: null,
      hasShownExtendableConf: false,
    })
  );
};

export const thunkHandleMountingRailPopup: Thunk<any> =
  (params: MountingRailThunkParams) => (dispatch, getState) => {
    const wasPending = selectCuttableMountingRailHintCheckPending(getState());
    wasPending &&
      dispatch(actionSetCuttableMountingRailHintCheckPending(false));

    const protrusionThreshold =
      constants.CUTTABLE_MOUNTING_RAIL_PROTRUSION_THRESHOLD || Infinity;

    const tac = params?.tac || selectTac(getState());
    const popUpsState = selectPopups(getState());
    const confDialog = selectConfDialog(getState());
    const confDialogIsClosed = !confDialog.open;
    if (
      tac &&
      !popUpsState.hasShownCuttableMountingRailHint &&
      (params?.omitExtendedConfClosedCheck || confDialogIsClosed)
    ) {
      const cuttableMountingRailData = range.getCuttableMountingRailData(tac);

      const sumOfWidthsOf = (itemsArray: any) =>
        itemsArray.reduce(
          (sum: number, currentItem: any) => (sum += currentItem.width),
          0
        );

      const candidates: any = [];
      cuttableMountingRailData.forEach((group: any) => {
        const { mountingRails, areaToCoverStartX, areaToCoverEndX } = group;
        const lengthOfMountingRailGroup = sumOfWidthsOf(mountingRails);
        const leftmostRailInGroup = mountingRails[0];
        const rightmostRailInGroup = mountingRails[mountingRails.length - 1];
        const mountingRailsStartX = mountingRails[0].x;
        const mountingRailsEndX =
          mountingRailsStartX + lengthOfMountingRailGroup;
        const protrusionLeft = areaToCoverStartX - mountingRailsStartX;
        const protrusionRight = mountingRailsEndX - areaToCoverEndX;
        const canCutLeft = protrusionLeft > protrusionThreshold;
        const canCutRight = protrusionRight > protrusionThreshold;
        const distanceToLeftWall = mountingRailsStartX;
        const distanceToRightWall = tac.wall?.size?.width
          ? tac.wall.size.width - mountingRailsEndX
          : 0;

        canCutLeft &&
          candidates.push({
            railItem: leftmostRailInGroup,
            distanceToWall: distanceToLeftWall,
            hintAlignment: LEFT,
          });
        canCutRight &&
          candidates.push({
            railItem: rightmostRailInGroup,
            distanceToWall: distanceToRightWall,
            hintAlignment: RIGHT,
          });
      });

      const chosenCandidate = candidates.reduce(
        (bestSoFar: any, candidate: any) =>
          candidate.distanceToWall > (bestSoFar?.distanceToWall || -Infinity)
            ? candidate
            : bestSoFar,
        null
      );

      const chosenMountingRail = chosenCandidate?.railItem;
      const hintAlignmentOfChosenMountingRail = chosenCandidate?.hintAlignment;

      /* The mounting rail object of the candidate is newly generated and doesn't
         have the itemid set, since it's not in the TAC. We need to find the
         corresponding mounting rail in the TAC before we proceed. */
      const chosenMountingRailInTac = chosenMountingRail
        ? tac.items.find(
            (tacItem: any) =>
              productsServiceCommon.isType(tacItem.id, ITEMS.MOUNTING_RAIL) &&
              tacItem.x === chosenMountingRail.x &&
              tacItem.y === chosenMountingRail.y &&
              tacItem.z === chosenMountingRail.z
          )
        : null;

      if (chosenMountingRailInTac) {
        dispatch(
          actionHandlePopupOnAddItem({
            showCuttableMountingRailHint: chosenMountingRailInTac,
            cuttableMountingRailHintAlignment:
              hintAlignmentOfChosenMountingRail,
            hasShownCuttableMountingRailHint: true,
            hasHadUserInteraction: true,
          })
        );
      }
    }
  };
